import {
  ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, afterNextRender, computed, inject, input, signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { DocumentoFormato } from './documento.types';
import { ExportarConversacionService } from './exportar-conversacion.service';

/** Icono de cada formato en el menú y en la bandeja. */
export const ICONO_FORMATO: Record<DocumentoFormato, string> = {
  pdf: 'pi-file-pdf', docx: 'pi-file-word', pptx: 'pi-desktop',
};

/** Margen mínimo del menú contra los bordes de la ventana y hueco con el botón. */
const MARGEN_PX = 8;
const HUECO_PX = 6;

let siguienteId = 0;

/**
 * Botón «Exportar conversación» con su menú de formatos. Se pinta en tres sitios —la
 * fila del listado lateral, la barra bajo la última respuesta y la página de historial—
 * y todos comparten el estado de `ExportarConversacionService`: si un hilo se está
 * exportando, cualquier botón suyo enseña el spinner.
 *
 * El menú es un popover con `position: fixed` que se cuelga de `<body>` al abrirse. En la
 * fila del rail (248 px, con `overflow` y transformaciones en los ancestros) cualquier
 * otra cosa sale recortada: era justo lo que pasaba con los botones en línea de antes.
 * Abre hacia abajo si cabe y, si no, hacia arriba; teclado como un menú ARIA (flechas,
 * Inicio/Fin, Esc devuelve el foco al botón).
 */
@Component({
  selector: 'sg-exportar-conversacion',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.sg-export--abierto]': 'abierto()',
    '[class.sg-export--ocupado]': 'ocupado()',
  },
  template: `
    @if (svc.formatos().length) {
      <button #disparador type="button" class="sg-export__btn" [class.sg-export__btn--barra]="variante() === 'barra'"
        (click)="alternar($event)" (keydown)="teclaDisparador($event)"
        aria-haspopup="menu" [attr.aria-expanded]="abierto()" [attr.aria-controls]="abierto() ? menuId : null"
        [title]="(ocupado() ? 'agent.documentos.exportando' : 'agent.documentos.' + clavesTexto()) | translate"
        [attr.aria-label]="(ocupado() ? 'agent.documentos.exportando' : 'agent.documentos.' + clavesTexto()) | translate"
        [attr.data-testid]="texto() ? 'message-export' : 'thread-export'">
        <i class="pi" [class.pi-file-export]="!ocupado()" [class.pi-spinner]="ocupado()" [class.pi-spin]="ocupado()"></i>
      </button>
    }
    @if (abierto()) {
      <div #menu class="sg-export-menu" role="menu" [id]="menuId" [class.is-listo]="posicion().lista"
        [style.top.px]="posicion().top" [style.left.px]="posicion().left"
        [attr.aria-label]="'agent.documentos.' + clavesTexto() + 'Como' | translate"
        (keydown)="teclaMenu($event)" data-testid="export-menu">
        <div class="sg-export-menu__titulo" aria-hidden="true">{{ 'agent.documentos.' + clavesTexto() + 'Como' | translate }}</div>
        @for (f of svc.formatos(); track f) {
          <button type="button" role="menuitem" class="sg-export-menu__item"
            [disabled]="svc.generando(threadId(), f, texto())" (click)="elegir(f)" [attr.data-testid]="'export-' + f">
            <span class="sg-export-menu__icono" aria-hidden="true">
              <i class="pi" [class]="svc.generando(threadId(), f, texto()) ? 'pi-spinner pi-spin' : iconos[f]"></i>
            </span>
            <span class="sg-export-menu__texto">
              <span class="sg-export-menu__nombre">{{ 'agent.documentos.formato.' + f | translate }}</span>
              <span class="sg-export-menu__hint">
                {{ (svc.generando(threadId(), f, texto()) ? 'agent.documentos.generando' : 'agent.documentos.exportarHint.' + f) | translate }}
              </span>
            </span>
          </button>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: inline-flex; }

    /* Base discreta: en la fila del listado manda el estilo de sus acciones
       (.thread-actions button, global); aquí solo lo imprescindible. */
    .sg-export__btn {
      display: inline-flex; align-items: center; justify-content: center;
      background: transparent; border: none; padding: 0; cursor: pointer; color: inherit;
      -webkit-tap-highlight-color: transparent;
    }
    .sg-export__btn:focus-visible { outline: 1px solid rgba(168, 197, 240, 0.6); outline-offset: 1px; }

    /* Variante «barra»: misma pieza que copiar / 👍 / 👎 de sg-feedback-bar. */
    .sg-export__btn--barra {
      width: 30px; height: 30px; border-radius: 8px;
      color: var(--sg-chat-ink-faint, #6b7280);
      transition: background 0.15s, color 0.15s;
    }
    .sg-export__btn--barra .pi { font-size: 14px; }
    .sg-export__btn--barra:hover,
    :host(.sg-export--abierto) .sg-export__btn--barra { background: var(--sg-chat-border, #1f1f1f); color: var(--sg-chat-ink, #e5e7eb); }
    :host(.sg-export--ocupado) .sg-export__btn--barra { color: #A8C5F0; }

    /* Menú: flota sobre todo (también sobre el diálogo móvil del listado). */
    .sg-export-menu {
      position: fixed; z-index: 2100; box-sizing: border-box;
      width: 264px; padding: 6px;
      background: #141416; border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 12px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
      font-family: 'Urbanist', 'Inter', sans-serif;
      opacity: 0; transform: translateY(-2px);
      transition: opacity 0.12s ease, transform 0.12s ease;
    }
    .sg-export-menu.is-listo { opacity: 1; transform: none; }
    .sg-export-menu__titulo {
      padding: 6px 10px 6px; font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase;
      color: rgba(255, 255, 255, 0.52);
    }
    .sg-export-menu__item {
      display: flex; align-items: center; gap: 12px; width: 100%;
      padding: 8px 10px; border: none; border-radius: 8px; background: transparent;
      color: #ffffff; text-align: left; cursor: pointer; font: inherit;
      transition: background 0.12s ease;
    }
    .sg-export-menu__item:hover:not(:disabled),
    .sg-export-menu__item:focus-visible { background: #1f1f22; outline: none; }
    .sg-export-menu__item:focus-visible { box-shadow: inset 0 0 0 1px rgba(168, 197, 240, 0.6); }
    .sg-export-menu__item:disabled { cursor: default; }
    .sg-export-menu__icono {
      flex: none; display: inline-flex; align-items: center; justify-content: center;
      width: 32px; height: 32px; border-radius: 8px;
      background: #0e0e10; border: 1px solid rgba(255, 255, 255, 0.10);
      color: rgba(255, 255, 255, 0.72); font-size: 14px;
    }
    .sg-export-menu__item:hover:not(:disabled) .sg-export-menu__icono,
    .sg-export-menu__item:focus-visible .sg-export-menu__icono { color: #A8C5F0; border-color: rgba(168, 197, 240, 0.35); }
    .sg-export-menu__item:disabled .sg-export-menu__icono { color: #A8C5F0; }
    .sg-export-menu__texto { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .sg-export-menu__nombre { font-size: 13.5px; font-weight: 600; }
    .sg-export-menu__hint { font-size: 12px; color: rgba(255, 255, 255, 0.52); }

    @media (prefers-reduced-motion: reduce) {
      .sg-export-menu { transition: none; transform: none; }
    }
  `],
})
export class ExportarConversacionComponent implements OnDestroy {
  readonly threadId = input.required<string>();
  /** Título visible de la conversación: lo enseña la bandeja mientras se genera. */
  readonly titulo = input('');
  /** Lo que se ve de UNA respuesta del agente: con él el botón genera el documento de esa
   *  respuesta; sin él, exporta la conversación entera. */
  readonly texto = input('');
  /** `fila`: lo estiliza el listado que lo aloja. `barra`: como los botones de feedback. */
  readonly variante = input<'fila' | 'barra'>('fila');

  protected readonly svc = inject(ExportarConversacionService);
  private readonly injector = inject(Injector);
  private readonly disparador = viewChild<ElementRef<HTMLButtonElement>>('disparador');
  private readonly menu = viewChild<ElementRef<HTMLElement>>('menu');

  protected readonly iconos = ICONO_FORMATO;
  protected readonly menuId = `sg-export-menu-${++siguienteId}`;
  protected readonly abierto = signal(false);
  protected readonly posicion = signal({ top: -9999, left: -9999, lista: false });
  protected readonly ocupado = computed(() => this.svc.exportando(this.threadId(), this.texto()));
  protected readonly clavesTexto = computed(() => (this.texto() ? 'generar' : 'exportar'));

  /** Cierra el menú con un clic fuera, un scroll o un cambio de tamaño de la ventana. */
  private readonly alPulsarFuera = (ev: Event) => {
    const t = ev.target as Node | null;
    if (t && (this.menu()?.nativeElement.contains(t) || this.disparador()?.nativeElement.contains(t))) return;
    this.cerrar(false);
  };
  private readonly alDesplazar = (ev: Event) => {
    if (this.menu()?.nativeElement.contains(ev.target as Node)) return;
    this.cerrar(false);
  };
  private readonly alRedimensionar = () => this.cerrar(false);

  constructor() {
    this.svc.cargarFormatos();
  }

  ngOnDestroy(): void {
    this.quitarEscuchas();
  }

  alternar(ev: Event): void {
    // Vive dentro de filas clicables (abrir la conversación): el clic es solo del botón.
    ev.stopPropagation();
    if (this.abierto()) this.cerrar(true);
    else this.abrir();
  }

  teclaDisparador(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation();
      if (!this.abierto()) this.abrir();
    } else if (ev.key === 'Enter' || ev.key === ' ') {
      // Que el Enter no llegue a la fila (la abriría) ni se convierta en otro clic.
      ev.stopPropagation();
    }
  }

  teclaMenu(ev: KeyboardEvent): void {
    const items = this.itemsActivos();
    const actual = items.indexOf(document.activeElement as HTMLButtonElement);
    let destino: number | null = null;
    switch (ev.key) {
      case 'ArrowDown': destino = (actual + 1) % items.length; break;
      case 'ArrowUp': destino = (actual - 1 + items.length) % items.length; break;
      case 'Home': destino = 0; break;
      case 'End': destino = items.length - 1; break;
      case 'Escape': ev.preventDefault(); ev.stopPropagation(); this.cerrar(true); return;
      case 'Tab': this.cerrar(false); return;
      default: return;
    }
    ev.preventDefault();
    items[destino]?.focus();
  }

  elegir(formato: DocumentoFormato): void {
    this.cerrar(true);
    void this.svc.exportar(this.threadId(), this.titulo(), formato, this.texto());
  }

  private abrir(): void {
    this.posicion.set({ top: -9999, left: -9999, lista: false });
    this.abierto.set(true);
    afterNextRender(() => {
      const menu = this.menu()?.nativeElement;
      if (!menu) return;
      document.body.appendChild(menu);
      this.colocar(menu);
      this.itemsActivos()[0]?.focus({ preventScroll: true });
      document.addEventListener('pointerdown', this.alPulsarFuera, true);
      window.addEventListener('scroll', this.alDesplazar, true);
      window.addEventListener('resize', this.alRedimensionar);
    }, { injector: this.injector });
  }

  private cerrar(devolverFoco: boolean): void {
    if (!this.abierto()) return;
    this.quitarEscuchas();
    this.abierto.set(false);
    if (devolverFoco) this.disparador()?.nativeElement.focus({ preventScroll: true });
  }

  private quitarEscuchas(): void {
    document.removeEventListener('pointerdown', this.alPulsarFuera, true);
    window.removeEventListener('scroll', this.alDesplazar, true);
    window.removeEventListener('resize', this.alRedimensionar);
  }

  /**
   * Debajo del botón, alineado a su borde izquierdo; si no cabe, a la derecha o encima.
   * En móvil el límite inferior es la barra de navegación (`.sg-bottombar`, fija y con
   * z-index 10000): un menú que acabara debajo de ella quedaría tapado.
   */
  private colocar(menu: HTMLElement): void {
    const boton = this.disparador()?.nativeElement;
    if (!boton) return;
    const r = boton.getBoundingClientRect();
    const ancho = menu.offsetWidth;
    const alto = menu.offsetHeight;
    const vw = window.innerWidth;
    const barra = document.querySelector('.sg-bottombar')?.getBoundingClientRect();
    const vh = barra && barra.height > 0 ? Math.min(barra.top, window.innerHeight) : window.innerHeight;

    let left = r.left;
    if (left + ancho > vw - MARGEN_PX) left = r.right - ancho;
    left = Math.max(MARGEN_PX, Math.min(left, vw - ancho - MARGEN_PX));

    let top = r.bottom + HUECO_PX;
    if (top + alto > vh - MARGEN_PX && r.top - HUECO_PX - alto >= MARGEN_PX) top = r.top - HUECO_PX - alto;
    top = Math.max(MARGEN_PX, Math.min(top, vh - alto - MARGEN_PX));

    this.posicion.set({ top, left, lista: true });
  }

  private itemsActivos(): HTMLButtonElement[] {
    const menu = this.menu()?.nativeElement;
    return menu ? Array.from(menu.querySelectorAll<HTMLButtonElement>('.sg-export-menu__item:not(:disabled)')) : [];
  }
}
